import { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ClampedText, fmtDateTimeShort } from '../../shared/listCells';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  fetchQueue,
  fetchAccounts,
  fetchCompanies,
  processQueue,
  checkTranseuLogin,
  setCompanyMasking,
  maskingModeLabel,
  testModeText,
  accountStateLabel,
  ENVIRONMENT_PRODUCTION,
  EXCHANGE_LABELS,
  EXCHANGE_TIMOCOM,
  EXCHANGE_TRANSEU,
  POSTING_STATUS_LABELS,
  maskingModesFor,
  fleetVariantsWarningText,
  type QueueFilters,
  type ExchangeAccounts,
  type Publication,
  type CompanyCell,
  type TranseuLoginCheck,
} from './publicationsApi';
import { PublicationActions } from './PublicationActions';
import { ExchangeName, PostingStatus, PublicationOutcome } from './PublicationRow';
import { menuLabel } from '../menuConfig';
import { PermissionGate, useHasPermission } from '../PermissionGate';
import { plural } from '../../shared/plural';
import { useFeature } from '../featureFlags/FeatureGate';

const PAGE_SIZE = 25;

/** Pusta opcja trybu lokalizacji spółki — tryb wybiera się przy każdej publikacji. */
const NO_COMPANY_MASKING = 'nie ustawiono — wybór przy każdej publikacji';

/**
 * Kolejka publikacji — wszystkie publikacje wszystkich zleceń.
 *
 * Port ekranu „Kolejka publikacji" ze źródła, bez trzech rzeczy: przycisków
 * per giełda (u nas jedna), „wyczyść nieudane" (nieudana zostaje z powodem —
 * to informacja, nie śmieć) i przetwarzania z rozszerzenia przeglądarki.
 *
 * „Przetwórz teraz" to narzędzie diagnostyczne dla administratora: kolejka
 * i tak leci co minutę. Serwer odmawia bez sekcji administracyjnej.
 */
export const PublicationQueue = () => {
  const notify = useNotify();
  // Tryb „przybliżony” spółki (16.10.2026, pyt. 19) — tylko z przełącznikiem.
  const nearbyOn = useFeature('maskingNearbyLocality');
  const [filters, setFilters] = useState<QueueFilters>({});
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Publication[] | null>(null);
  const [total, setTotal] = useState(0);
  const [accounts, setAccounts] = useState<ExchangeAccounts | null>(null);
  const [transeuAccounts, setTranseuAccounts] = useState<ExchangeAccounts | null>(null);
  const [companies, setCompanies] = useState<CompanyCell[] | null>(null);
  const [busy, setBusy] = useState(false);

  const loadSettings = useCallback(() => {
    fetchCompanies()
      .then(setCompanies)
      .catch(() => setCompanies(null));
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  /**
   * Tryb maskowania per spółka (I.09). Serwer odmawia bez sekcji
   * administracyjnej — odmowę pokazujemy w całości, nie chowamy przycisku.
   */
  const changeMasking = async (s: CompanyCell, mode: string) => {
    try {
      await setCompanyMasking(s.id, mode || null);
      notify(
        mode
          ? `Zapisano lokalizację na giełdzie dla ${s.name}: ${maskingModeLabel(mode)}.`
          : `Usunięto ustawienie lokalizacji na giełdzie dla ${s.name} — tryb będzie wybierany przy każdej publikacji.`,
        { type: 'success' },
      );
      loadSettings();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  };

  const load = useCallback(async () => {
    try {
      const result = await fetchQueue(filters, {
        start: page * PAGE_SIZE,
        end: page * PAGE_SIZE + PAGE_SIZE - 1,
      });
      setRows(result.rows);
      setTotal(result.total);
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [filters, page, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    fetchAccounts()
      .then(setAccounts)
      .catch(() => setAccounts(null));
    fetchAccounts(EXCHANGE_TRANSEU)
      .then(setTranseuAccounts)
      .catch(() => setTranseuAccounts(null));
  }, []);

  const processNow = async () => {
    setBusy(true);
    try {
      const results = await processQueue();
      notify(
        results.length === 0
          ? 'Brak publikacji oczekujących na wysłanie.'
          : `Przetworzono ${plural(results.length, 'publikację', 'publikacje', 'publikacji')}: ${results
              .map((result) => `#${result.id} — ${(POSTING_STATUS_LABELS[result.status] ?? result.status).toLowerCase()}`)
              .join(', ')}.`,
        { type: 'info' },
      );
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const notifyOperator = (message: string, type: 'success' | 'error' | 'warning') => notify(message, { type });
  const changeFilter = (change: QueueFilters) => {
    setPage(0);
    setFilters((f) => ({ ...f, ...change }));
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Tytuł w niebieskim pasku, jak na ekranach z `<List>` — nagłówek
          w treści zostawiał pasek pusty (e2e na dev, 28.09.2026). */}
      <Title title={menuLabel('postings')} />

      {/* Tryb testowy tylko przy koncie, z którym da się w nim pracować —
          bez kompletu danych żadnej spółki wiersz kont niżej mówi, czego
          brakuje (omówienie 01.10.2026: bez „piaskownicy” i „sandbox”). */}
      {accounts &&
        accounts.environment !== ENVIRONMENT_PRODUCTION &&
        accounts.companies.some((c) => c.complete) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {testModeText(EXCHANGE_TIMOCOM)}
          </Alert>
        )}
      {accounts && <AccountsRow label="Konta TIMOCOM" accounts={accounts} />}
      {transeuAccounts && <AccountsRow label="Konta Trans.eu" accounts={transeuAccounts} transeu />}

      {/* Tryb maskowania spółki zmienia sekcja administracyjna (API: 403) —
          bez niej ukryte, nie wyszarzone (role działowe, 01.10.2026). */}
      <PermissionGate permission="ADMIN_SECTION">
      {companies && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
          <Typography variant="caption" color="text.secondary">
            Jak pokazywać lokalizację na giełdzie:
          </Typography>
          {companies.map((s) => (
            <FormControl key={s.id} size="small" fullWidth={false} sx={{ minWidth: 300 }}>
              <InputLabel id={`maskowanie-${s.id}`} shrink>
                {s.name}
              </InputLabel>
              <Select
                labelId={`maskowanie-${s.id}`}
                label={s.name}
                notched
                displayEmpty
                value={s.locationMasking ?? ''}
                renderValue={(v) => (v ? maskingModeLabel(String(v)) : NO_COMPANY_MASKING)}
                onChange={(e) => void changeMasking(s, String(e.target.value))}
              >
                <MenuItem value="">{NO_COMPANY_MASKING}</MenuItem>
                {maskingModesFor(nearbyOn).map((t) => (
                  <MenuItem key={t.kod} value={t.kod}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Stack>
      )}
      </PermissionGate>

      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <FormControl size="small" fullWidth={false} sx={{ minWidth: 160 }}>
          <InputLabel id="filtr-status">Stan</InputLabel>
          <Select
            labelId="filtr-status"
            label="Stan"
            value={filters.status ?? ''}
            onChange={(e) => changeFilter({ status: String(e.target.value) })}
          >
            <MenuItem value="">wszystkie</MenuItem>
            {Object.entries(POSTING_STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth={false} sx={{ minWidth: 140 }}>
          <InputLabel id="filtr-gielda">Giełda</InputLabel>
          <Select
            labelId="filtr-gielda"
            label="Giełda"
            value={filters.exchange ?? ''}
            onChange={(e) => changeFilter({ exchange: String(e.target.value) })}
          >
            <MenuItem value="">wszystkie</MenuItem>
            {Object.entries(EXCHANGE_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          fullWidth={false}
          label="Numer zlecenia"
          value={filters.q ?? ''}
          onChange={(e) => changeFilter({ q: e.target.value })}
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => void load()}>
          Odśwież
        </Button>
        <PermissionGate permission="ADMIN_SECTION">
        <Button size="small" variant="outlined" disabled={busy} onClick={() => void processNow()}>
          Przetwórz teraz
        </Button>
        </PermissionGate>
        {busy && <CircularProgress size={18} />}
      </Stack>

      {rows === null && <CircularProgress size={24} />}
      {rows && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Brak publikacji spełniających wybrane filtry.
        </Typography>
      )}
      {rows && rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          {/* Nagłówki w jednej linii, czas bez sekund, trasa skracana
              z podpowiedzią — 11 kolumn ma się zmieścić od 1366 px bez
              przewijania (28.09.2026). Wynik giełdy zostaje w całości:
              braki i błędy mają być czytelne, więc tylko ograniczamy mu
              szerokość, a nie ucinamy. */}
          <Table
            size="small"
            sx={{
              '& th': { whiteSpace: 'nowrap' },
              '& .MuiTableCell-sizeSmall': { px: 1 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Zlecenie</TableCell>
                <TableCell>Relacja</TableCell>
                <TableCell>Giełda</TableCell>
                <TableCell>#</TableCell>
                <TableCell>Stan</TableCell>
                <TableCell>Zlecił</TableCell>
                <TableCell>Zlecono</TableCell>
                <TableCell>Wystawiono</TableCell>
                <TableCell>Wynik</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <a href={`#/user-offers/${p.userOfferId}/show`}>
                      {p.userOffer?.externalId ?? `#${p.userOfferId}`}
                    </a>
                    {/* Warianty floty (fala B, D6) — ostrzeżenie z odsyłaczem do karty. */}
                    {p.fleetVariants && p.fleetVariants.length > 0 && (
                      <Tooltip title={fleetVariantsWarningText(p.fleetVariants)}>
                        <Chip
                          size="small"
                          color="warning"
                          component="a"
                          clickable
                          href={`#/user-offers/${p.userOfferId}/show`}
                          label={`warianty floty: ${p.fleetVariants.length}`}
                          data-testid={`queue-fleet-variants-${p.id}`}
                          sx={{ ml: 0.5, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <ClampedText
                      text={
                        p.userOffer
                          ? `${p.userOffer.fromCountry} ${p.userOffer.fromCity ?? ''} → ${p.userOffer.toCountry} ${p.userOffer.toCity ?? ''}`
                          : null
                      }
                      maxWidth={{ xs: 200, xl: 320 }}
                    />
                  </TableCell>
                  <TableCell>
                    <ExchangeName exchange={p.exchange} />
                  </TableCell>
                  <TableCell>{p.postingNumber}</TableCell>
                  <TableCell>
                    <PostingStatus status={p.status} />
                  </TableCell>
                  <TableCell>{p.requestedBy?.username ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTimeShort(p.requestedAt)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTimeShort(p.postedAt)}</TableCell>
                  <TableCell sx={{ minWidth: 180, maxWidth: 320 }}>
                    <PublicationOutcome publication={p} />
                  </TableCell>
                  <TableCell align="right">
                    <PublicationActions publication={p} onChanged={load} notify={notifyOperator} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, s) => setPage(s)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
          />
        </Box>
      )}
    </Box>
  );
};

/** Które spółki mają komplet poświadczeń giełdy — bez wartości, tylko fakt. */
/**
 * Konta giełdy w magazynie poświadczeń. Dla Trans.eu dodatkowo: stan blokady
 * wystawiania (25.09.2026) i „Sprawdź logowanie” — sam token OAuth, bez
 * publikacji — żeby dało się zweryfikować wpisane dane bez ryzyka.
 */
const AccountsRow = ({
  label,
  accounts,
  transeu = false,
}: {
  label: string;
  accounts: ExchangeAccounts;
  transeu?: boolean;
}) => {
  const notify = useNotify();
  // Nazwy kluczy magazynu tylko dla administratora — on je wpisuje (07.10.2026).
  const isAdmin = useHasPermission('ADMIN_SECTION');
  const [checks, setChecks] = useState<Record<number, TranseuLoginCheck | 'busy'>>({});
  const check = async (companyId: number) => {
    setChecks((c) => ({ ...c, [companyId]: 'busy' }));
    try {
      const r = await checkTranseuLogin(companyId);
      setChecks((c) => ({ ...c, [companyId]: r }));
    } catch (e) {
      setChecks((c) => {
        const next = { ...c };
        delete next[companyId];
        return next;
      });
      notify(toErrorMessage(e), { type: 'error' });
    }
  };
  return (
    <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
      <Typography variant="caption" color="text.secondary">
        {label} (dane dostępowe):
      </Typography>
      {transeu && accounts.publishingEnabled === false && (
        <Chip size="small" color="error" label="wystawianie zablokowane" />
      )}
      {accounts.companies.length === 0 && (
        <Chip size="small" color="error" variant="outlined" label="żadna spółka nie ma danych dostępowych" />
      )}
      {accounts.companies.map((s) => {
        const state = s.companyId === null ? undefined : checks[s.companyId];
        return (
          <Stack key={String(s.companyId)} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Chip
              size="small"
              color={s.complete ? 'success' : 'warning'}
              variant="outlined"
              label={accountStateLabel(s, isAdmin)}
            />
            <PermissionGate permission="ADMIN_SECTION">
            {transeu && s.complete && s.companyId !== null && (
              <Button
                size="small"
                disabled={state === 'busy'}
                onClick={() => void check(s.companyId as number)}
              >
                Sprawdź logowanie
              </Button>
            )}
            </PermissionGate>
            {state && state !== 'busy' && (
              // Szczegóły techniczne odmowy w dymku — ekran administracyjny
              // (przycisk sprawdzenia widzi tylko administrator), 07.10.2026.
              <Tooltip title={state.errorDetails ? `Szczegóły techniczne: ${state.errorDetails}` : ''}>
                <Chip
                  size="small"
                  color={state.ok ? 'success' : 'error'}
                  label={state.ok ? 'logowanie udane' : `logowanie nieudane: ${state.error ?? ''}`}
                />
              </Tooltip>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
